import { useState, useMemo, useEffect } from 'react';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { newsletterAPI } from '../../api/endpoints';
import { getRecaptchaToken, preloadRecaptcha } from '../../utils/recaptcha';
import RecaptchaLegalNotice from '../RecaptchaLegalNotice';

const createNewsletterSchema = (t) =>
  z.object({
    email: z
      .string()
      .trim()
      .min(1, t('home.download_app.email_required', 'Veuillez entrer votre email'))
      .email(t('home.download_app.email_invalid', 'Adresse email invalide')),
  });

const DownloadAppCard = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const schema = useMemo(() => createNewsletterSchema(t), [t]);

  useEffect(() => {
    preloadRecaptcha();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = schema.safeParse({ email });
    if (!result.success) {
      setError(result.error.issues[0]?.message || '');
      return;
    }
    const trimmed = result.data.email;
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const recaptchaToken = await getRecaptchaToken('newsletter_subscribe');
      const response = await newsletterAPI.subscribe(trimmed, recaptchaToken);
      const body = response.data ?? response;
      const data = body.data ?? body;
      const apiMessage = body.message;
      if (data?.alreadySubscribed) {
        setMessage(apiMessage || t('home.download_app.already_subscribed'));
      } else {
        setMessage(apiMessage || t('home.download_app.success'));
      }
      setEmail('');
    } catch (err) {
      const apiMessage = err.response?.data?.message || err.message;
      setError(apiMessage || t('home.download_app.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-100 flex justify-center mt-6 pt-6 rounded-xl mb-10">
      <div className="flex flex-col md:flex-row items-end xl:gap-6">
        <img className="h-96 md:w-[44vw]" src="/images/app.png" alt="app" />
        <div className="lg:max-w-xl flex flex-col gap-10 py-12 px-6 lg:pl-0 lg:pr-6">
          <p className="text-4xl xl:text-5xl leading-[46px]">
            {t('home.download_app.title')}{' '}
            <span className="text-orange-hover font-medium">{t('home.download_app.title_highlight')}</span>{' '}
            {t('home.download_app.title_suffix')}
          </p>

          <form onSubmit={handleSubmit} className="flex flex-col gap-2 w-full">
            <div className="flex w-full">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('home.download_app.email_placeholder')}
                className="w-full px-5 py-2.5 border-2 border-gray-200 border-r-0 rounded-l-full focus:ring-2 focus:ring-red-500 outline-none"
                required
                disabled={loading}
              />
              <button
                type="submit"
                disabled={loading}
                className="text-white bg-red-500 px-6 rounded-r-full w-32 font-medium hover:bg-red-600 transition disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? '...' : t('home.download_app.subscribe_button')}
              </button>
            </div>
            {message && (
              <p className="text-xs text-green-700" role="status">
                {message}
              </p>
            )}
            {error && (
              <p className="text-sm text-red-600" role="alert">
                {error}
              </p>
            )}
            <RecaptchaLegalNotice />
          </form>

          <div className="flex items-center gap-6">
            <img src="/images/google-play.png" alt="googleplay" />
            <img src="/images/app-store.png" alt="appstore" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DownloadAppCard;
